// M1 THROWAWAY (OPTI-6761): pre-flight workspace schema snapshot for the
// Live Analytics chat system prompt. Issues 2 calls to the prototype
// `/metrics/api/v1/query` endpoint (workspace-services PR #12620) and
// formats a markdown block listing the top `strings`/`numerics` MAP keys
// for the current flow. Delete once the proper agent lifecycle lands.

import axios from "axios";

const METRICS_QUERY_PATH = "/metrics/api/v1/query";
const FLOW_ID_PATTERN = /^[0-9a-fA-F-]{36}$/;

type MetricsQueryResponse = {
  results: Record<string, { rows: ReadonlyArray<ReadonlyArray<unknown>> }>;
};

const sanitizeFlowKey = (flowId: string): string =>
  `f${flowId.replace(/[^a-zA-Z0-9]/g, "")}`;

const formatKeyList = (rows: ReadonlyArray<ReadonlyArray<unknown>>): string =>
  rows.length === 0
    ? "_(none)_"
    : rows.map((r) => `\`${String(r[0])}\` (n=${String(r[1])})`).join(", ");

export const fetchWorkspaceSchemaBlock = async (
  baseUrl: string,
  flowId: string,
): Promise<string> => {
  // Hard-reject anything that isn't a UUID before string-interpolating into
  // SQL. The metrics endpoint is auth-gated, but breaking the query on a
  // weird flowId would still produce a confusing prompt rather than a clean
  // fallback.
  if (!FLOW_ID_PATTERN.test(flowId)) {
    throw new Error(`Invalid flowId for schema snapshot: ${flowId}`);
  }

  const endpoint = `https://${baseUrl}${METRICS_QUERY_PATH}`;
  const safe = sanitizeFlowKey(flowId);

  // Volume check + MAP-key samples in a single batched request.
  const queries = {
    volume: `SELECT COUNT(*) AS n FROM flow_data_prototype WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '7 days'`,
    [`s_${safe}`]: `SELECT key, COUNT(*) AS n FROM (SELECT strings FROM flow_data_prototype WHERE flow_id = '${flowId}' ORDER BY start_datetime DESC LIMIT 1000) sample, UNNEST(map_keys(sample.strings)) AS t(key) GROUP BY 1 ORDER BY 2 DESC LIMIT 20`,
    [`n_${safe}`]: `SELECT key, COUNT(*) AS n FROM (SELECT numerics FROM flow_data_prototype WHERE flow_id = '${flowId}' ORDER BY start_datetime DESC LIMIT 1000) sample, UNNEST(map_keys(sample.numerics)) AS t(key) GROUP BY 1 ORDER BY 2 DESC LIMIT 20`,
  };

  const { data } = await axios.post<MetricsQueryResponse>(endpoint, {
    queries,
  });
  const volumeRows = data.results?.volume?.rows ?? [];
  const stringRows = data.results?.[`s_${safe}`]?.rows ?? [];
  const numericRows = data.results?.[`n_${safe}`]?.rows ?? [];

  const volume = volumeRows[0]?.[0];
  if (volume == null || Number(volume) === 0) {
    return `_(no decisions for this flow in the last 7 days — agent will discover keys on demand)_`;
  }

  return [
    "**Known output keys (sampled at session start).** Top 20 `strings` and `numerics` keys for this flow, sampled from the 1000 most recent decisions. Reference these directly — no need to re-run discovery unless you hit a key you don't see here.",
    "",
    `- \`strings\` keys: ${formatKeyList(stringRows)}`,
    `- \`numerics\` keys: ${formatKeyList(numericRows)}`,
    "",
    "Prefer `numerics` for aggregation (`AVG`, `quantile_cont`, etc.) and `strings` for categorical / label lookups. Many keys are dual-mapped — `strings[k]` holds the first 40 chars of the raw value, `numerics[k]` holds the numeric cast when castable.",
  ].join("\n");
};
